/**
 * The bottom tab bar below 1024px, and its More sheet.
 * Spec: `brand-redesign-docs/12-MOBILE-APP.md`.
 *
 * Four role tabs and More. The active tab sits on a pill that glides between
 * tabs; tapping the active tab scrolls the page back to the top. More opens a
 * bottom sheet with every other destination, Profile and the theme switch.
 * The sheet closes on the scrim, Esc, a drag down, or any navigation.
 */

import { AnimatePresence, motion, useReducedMotion, type PanInfo } from "framer-motion";
import { LayoutGrid, UserRound } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/lib/tenant";
import { useMessagesUnread } from "@/lib/api/messages";
import { useTheme } from "@/lib/theme";
import { ROUTES } from "@/types/routes";

import { GROUP_LABEL, isActivePath, labelFor, tabLabelFor, tabsFor, type NavDestination } from "./nav";
import { ThemeSwitch } from "./Sidebar";

const EASE = [0.22, 1, 0.36, 1] as const;

export function TabBar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const reduce = useReducedMotion();
  const [moreOpen, setMoreOpen] = useState(false);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  const unread = useMessagesUnread(!!user).data ?? 0;

  const { tabs, more } = tabsFor(user?.role, useTenant().features);
  const activeTab = tabs.find((t) => isActivePath(pathname, t.to));
  const moreActive = !activeTab && (more.some((m) => isActivePath(pathname, m.to)) || pathname === ROUTES.PROFILE);

  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  return (
    <>
      <nav className="fr-tabs" aria-label="Main">
        {tabs.map((item) => {
          const active = item === activeTab && !moreOpen;
          const Icon = item.icon;
          const count = item.to === ROUTES.MESSAGES ? unread : 0;
          const label = tabLabelFor(item, user?.role);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === ROUTES.DASHBOARD}
              className="fr-tab"
              data-active={active || undefined}
              aria-label={count > 0 ? `${label}, ${count} unread` : undefined}
              onClick={() => {
                // Tapping the tab you are on takes you back to the top.
                if (item === activeTab) window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
              }}
            >
              <TabInner Icon={Icon} label={label} active={active} count={count} animate={!reduce} />
            </NavLink>
          );
        })}
        <button
          type="button"
          className="fr-tab"
          data-active={moreActive || moreOpen || undefined}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((o) => !o)}
        >
          <TabInner Icon={LayoutGrid} label="More" active={moreActive || moreOpen} count={0} animate={!reduce} />
        </button>
      </nav>

      <MoreSheet open={moreOpen} onClose={closeMore} items={more} />
    </>
  );
}

function TabInner({
  Icon,
  label,
  active,
  count,
  animate,
}: {
  Icon: NavDestination["icon"];
  label: string;
  active: boolean;
  count: number;
  animate: boolean;
}) {
  return (
    <>
      <span className="fr-tab__icon">
        {active ? (
          <motion.span
            layoutId={animate ? "tab-pill" : undefined}
            className="fr-tab__pill"
            transition={{ type: "spring", stiffness: 520, damping: 38 }}
            aria-hidden
          />
        ) : null}
        <Icon size={21} strokeWidth={active ? 2 : 1.6} aria-hidden />
        {count > 0 ? (
          <span className="fr-tab__badge" aria-hidden>
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </span>
      <span className="fr-tab__label">{label}</span>
    </>
  );
}

function MoreSheet({ open, onClose, items }: { open: boolean; onClose: () => void; items: NavDestination[] }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const reduce = useReducedMotion();
  const { resolved, setMode } = useTheme();
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.({ preventScroll: true });
    };
  }, [open, onClose]);

  const groups = (["work", "people", "admin"] as const)
    .map((g) => ({ g, list: items.filter((i) => i.group === g) }))
    .filter((x) => x.list.length > 0);

  function onDragEnd(_: unknown, info: PanInfo) {
    if (info.offset.y > 90 || info.velocity.y > 600) onClose();
  }

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            className="fr-sheet-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.2 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            ref={sheetRef}
            className="fr-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="More"
            tabIndex={-1}
            initial={reduce ? { opacity: 0 } : { y: "100%" }}
            animate={reduce ? { opacity: 1 } : { y: 0 }}
            exit={reduce ? { opacity: 0 } : { y: "100%" }}
            transition={reduce ? { duration: 0 } : { duration: 0.36, ease: EASE }}
            drag={reduce ? false : "y"}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={onDragEnd}
          >
            <span className="fr-sheet__grab" aria-hidden />
            {groups.map(({ g, list }) => (
              <section key={g} className="fr-sheet__group">
                <p className="st-label fr-sheet__label">{g === "work" ? "More" : GROUP_LABEL[g]}</p>
                <div className="fr-sheet__grid">
                  {list.map((item) => (
                    <SheetTile
                      key={item.to}
                      to={item.to}
                      Icon={item.icon}
                      label={labelFor(item, user?.role)}
                      active={isActivePath(pathname, item.to)}
                    />
                  ))}
                </div>
              </section>
            ))}
            <section className="fr-sheet__group">
              <p className="st-label fr-sheet__label">You</p>
              <div className="fr-sheet__grid">
                <SheetTile to={ROUTES.PROFILE} Icon={UserRound} label="Profile" active={pathname === ROUTES.PROFILE} />
              </div>
            </section>
            <div className="fr-sheet__theme">
              <ThemeSwitch resolved={resolved} onPick={setMode} animate={!reduce} />
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function SheetTile({ to, Icon, label, active }: { to: string; Icon: NavDestination["icon"]; label: string; active: boolean }) {
  return (
    <NavLink to={to} className="fr-sheet__tile" data-active={active || undefined}>
      <span className="fr-sheet__icon">
        <Icon size={20} strokeWidth={1.6} aria-hidden />
      </span>
      <span className="fr-sheet__name">{label}</span>
    </NavLink>
  );
}
